import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Token } from './token';
export { Token };
import { tokenSchema } from '@schemas/business-objects/token';
import { MessageListenerFormArray } from './message-listener-form-group';
import { MyUserTaskFormArray } from './my-user-task-form-group';
import {
  ProcessInstanceFormArray,
  ProcessInstanceFormGroup,
} from './process-instance-form-group';
import {
  ServiceTaskFormArray,
  ServiceTaskFormGroup,
} from './service-task-form-group';
import { TimerFormArray } from './timer-form-group';
import { TokenHistoryFormArray } from './token-history-form-group';
import { TokenLinkFormArray } from './token-link-form-group';
import { UserTaskFormArray, UserTaskFormGroup } from './user-task-form-group';

export class TokenFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    referenceNumber: SchemaFormControl;
    activityId: SchemaFormControl;
    status: SchemaFormControl;
    errorMessage: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    processInstanceId: SchemaFormControl;
    processInstance: ProcessInstanceFormGroup;
    currentUserTaskId: SchemaFormControl;
    currentUserTask: UserTaskFormGroup;
    currentServiceTaskId: SchemaFormControl;
    currentServiceTask: ServiceTaskFormGroup;
    currentChildProcessInstanceId: SchemaFormControl;
    currentChildProcessInstance: ProcessInstanceFormGroup;
    userTasks: UserTaskFormArray;
    serviceTasks: ServiceTaskFormArray;
    childProcessInstances: ProcessInstanceFormArray;
    tokenHistories: TokenHistoryFormArray;
    downstreamLinks: TokenLinkFormArray;
    upstreamLinks: TokenLinkFormArray;
    timers: TimerFormArray;
    messageListeners: MessageListenerFormArray;
    myUserTasks: MyUserTaskFormArray;
  };

  declare value: Partial<Token>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(tokenSchema, Token, data, options);
  }

  get object(): Token {
    return new Token(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'processInstance':
        return new ProcessInstanceFormGroup();
      case 'currentUserTask':
        return new UserTaskFormGroup();
      case 'currentServiceTask':
        return new ServiceTaskFormGroup();
      case 'currentChildProcessInstance':
        return new ProcessInstanceFormGroup();
      case 'userTasks':
        return new UserTaskFormArray();
      case 'serviceTasks':
        return new ServiceTaskFormArray();
      case 'childProcessInstances':
        return new ProcessInstanceFormArray();
      case 'tokenHistories':
        return new TokenHistoryFormArray();
      case 'downstreamLinks':
        return new TokenLinkFormArray();
      case 'upstreamLinks':
        return new TokenLinkFormArray();
      case 'timers':
        return new TimerFormArray();
      case 'messageListeners':
        return new MessageListenerFormArray();
      case 'myUserTasks':
        return new MyUserTaskFormArray();
      default:
        return undefined;
    }
  }

  async updateState(options: any): Promise<any> {
    const instance = new Token(this.value);
    return instance.updateState(options);
  }

  async move(options: any): Promise<any> {
    const instance = new Token(this.value);
    return instance.move(options);
  }

  async fail(options: any): Promise<any> {
    const instance = new Token(this.value);
    return instance.fail(options);
  }
}

export class TokenFormArray extends PersistedFormArray {
  readonly entityName = 'Token' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(tokenSchema, Token, options);
  }

  protected override createItemGroup() {
    return new TokenFormGroup();
  }

  get array(): Token[] {
    return this.controls.map((group: TokenFormGroup) => group.object);
  }
}

export class TokenPersistedArray extends PersistedArray<Token> {
  readonly entityName = 'Token' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Token, options);
  }
}
